import { useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { compileTsx } from '../script/compileScript';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; Component: ComponentType<Record<string, unknown>> }
  | { status: 'error'; message: string };

/** Compile + import a TSX module, caching by source string. Debounced. */
export function useTsxModule(tsx: string): LoadState {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const lastSrc = useRef<string>('');
  useEffect(() => {
    // Empty source means there is nothing to compile (e.g. a tsxInstance with a
    // missing Main, where TsxRenderer passes tsx=''). Stay 'loading' rather than
    // compiling an empty string into a throwaway blob on every such mount.
    if (tsx === '') { setState({ status: 'loading' }); return; }
    // `state` is intentionally read stale here (omitted from the deps array):
    // worst case is one redundant recompile on an A→B→A revert, never a missed update.
    if (tsx === lastSrc.current && state.status === 'ready') return;
    let revoke = () => {};
    let cancelled = false;
    const t = setTimeout(() => {
      setState({ status: 'loading' });
      try {
        const compiled = compileTsx(tsx);
        revoke = compiled.revoke;
        import(/* @vite-ignore */ compiled.url)
          .then((mod) => {
            if (cancelled) return;
            const Component = (mod.default ?? mod.Component) as ComponentType<Record<string, unknown>> | undefined;
            if (!Component) { setState({ status: 'error', message: 'module has no default export' }); return; }
            lastSrc.current = tsx;
            setState({ status: 'ready', Component });
          })
          .catch((e) => { if (!cancelled) setState({ status: 'error', message: String(e?.message ?? e) }); });
      } catch (e) {
        setState({ status: 'error', message: String((e as Error)?.message ?? e) });
      }
    }, 150); // debounce live edits
    return () => { cancelled = true; clearTimeout(t); revoke(); };
  }, [tsx]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}
